import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { CartProvider } from "./context/CartContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Account from "./pages/Account";
import InfoPage from "./pages/InfoPage";
import BlogIndex from "./pages/BlogIndex";
import BlogDetail from "./pages/BlogDetail";

import { CustomerAuthProvider } from "./context/CustomerAuthContext";

// Admin
import { AdminAuthProvider } from "./admin/AdminAuthContext";
import AdminLayout from "./admin/AdminLayout";
import AdminLogin from "./admin/pages/Login";
import Dashboard from "./admin/pages/Dashboard";
import { ProductsList, ProductForm } from "./admin/pages/Products";
import { BlogList, BlogForm } from "./admin/pages/Blog";
import { OrdersList, OrderDetail } from "./admin/pages/Orders";
import Coupons from "./admin/pages/Coupons";
import Sections from "./admin/pages/Sections";
import Subscribers from "./admin/pages/Subscribers";
import Backups from "./admin/pages/Backups";
import Categories from "./admin/pages/Categories";
import AdminReviews from "./admin/pages/Reviews";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setP(max > 0 ? (window.scrollY / max) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-[2px] bg-transparent pointer-events-none" data-testid="scroll-progress">
      <div className="h-full bg-[#1A1A1A] transition-[width] duration-150" style={{ width: `${p}%` }} />
    </div>
  );
}

function PageFade({ children }) {
  const { pathname } = useLocation();
  const [key, setKey] = useState(pathname);
  useEffect(() => setKey(pathname), [pathname]);
  return <div key={key} className="page-fade">{children}</div>;
}

// Public storefront layout
function StorefrontShell({ children }) {
  return (
    <CustomerAuthProvider>
      <CartProvider>
        <ScrollToTop />
        <ScrollProgress />
        <Navbar />
        <CartDrawer />
        <main>
          <PageFade>{children}</PageFade>
        </main>
        <Footer />
      </CartProvider>
    </CustomerAuthProvider>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#c3ba9e]">
      <BrowserRouter>
        <Routes>
          {/* Admin routes — own layout, no storefront chrome */}
          <Route path="/admin/login" element={<AdminAuthProvider><AdminLogin /></AdminAuthProvider>} />
          <Route path="/admin" element={<AdminAuthProvider><AdminLayout /></AdminAuthProvider>}>
            <Route index element={<Dashboard />} />
            <Route path="orders" element={<OrdersList />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="products" element={<ProductsList />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:slug/edit" element={<ProductForm />} />
            <Route path="blog" element={<BlogList />} />
            <Route path="blog/new" element={<BlogForm />} />
            <Route path="blog/:slug/edit" element={<BlogForm />} />
            <Route path="coupons" element={<Coupons />} />
            <Route path="sections" element={<Sections />} />
            <Route path="subscribers" element={<Subscribers />} />
            <Route path="categories" element={<Categories />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="backups" element={<Backups />} />
          </Route>

          {/* Storefront routes */}
          <Route path="/" element={<StorefrontShell><Home /></StorefrontShell>} />
          <Route path="/shop" element={<StorefrontShell><Shop /></StorefrontShell>} />
          <Route path="/shop/:slug" element={<StorefrontShell><ProductDetail /></StorefrontShell>} />
          <Route path="/cart" element={<StorefrontShell><Cart /></StorefrontShell>} />
          <Route path="/checkout" element={<StorefrontShell><Checkout /></StorefrontShell>} />
          <Route path="/order-confirmation" element={<StorefrontShell><OrderConfirmation /></StorefrontShell>} />
          <Route path="/login" element={<StorefrontShell><AuthPage mode="login" /></StorefrontShell>} />
          <Route path="/signup" element={<StorefrontShell><AuthPage mode="signup" /></StorefrontShell>} />
          <Route path="/auth/callback" element={<StorefrontShell><AuthCallback /></StorefrontShell>} />
          <Route path="/forgot-password" element={<StorefrontShell><ForgotPassword /></StorefrontShell>} />
          <Route path="/reset-password" element={<StorefrontShell><ResetPassword /></StorefrontShell>} />
          <Route path="/account" element={<StorefrontShell><Account /></StorefrontShell>} />
          <Route path="/blog" element={<StorefrontShell><BlogIndex /></StorefrontShell>} />
          <Route path="/blog/:slug" element={<StorefrontShell><BlogDetail /></StorefrontShell>} />
          <Route path="/shipping" element={<StorefrontShell><InfoPage slug="shipping" /></StorefrontShell>} />
          <Route path="/returns" element={<StorefrontShell><InfoPage slug="returns" /></StorefrontShell>} />
          <Route path="/size-guide" element={<StorefrontShell><InfoPage slug="size-guide" /></StorefrontShell>} />
          <Route path="/garment-care" element={<StorefrontShell><InfoPage slug="garment-care" /></StorefrontShell>} />
        </Routes>

        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#c3ba9e",
              color: "#1A1A1A",
              border: "1px solid #a89e80",
              borderRadius: "0",
              fontFamily: "DM Sans, sans-serif",
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
